import type { FeedFilters } from '#services/api/mia.ts';
import type { InsightsSavedView } from '#services/api/saved-views.ts';
import type { getParsedSearchParams, HomeTab } from '../Home.utils.ts';

import { allProvidersToken } from '#utils/provider-filter.ts';
import { paramByFilterKey, tabFilterPolicy } from '../Home.utils.ts';

/**
 * Reserved name of the per-user system-managed saved view that remembers the Coordinate tab's
 * last-used filters. It rides the ordinary saved-view rails — applied through the `?view=` token
 * like any Scheduling view — but never renders as a chip (`coordinate` views carry no view UI).
 */
export const coordinateAutoViewName = '__coordinate-auto-view__';

/** The user's auto-view, if they have one. */
export function findCoordinateAutoView(views: InsightsSavedView[]): InsightsSavedView | undefined {
  return views.find(
    (view) => view.tabType === 'coordinate' && view.name === coordinateAutoViewName
  );
}

// The Coordinate tab's URL filter params, derived from its filter policy — the entry carry-list
// below can't drift from what the tab actually reads.
const coordinateFilterParams = tabFilterPolicy.coordinate.keys.map((key) => paramByFilterKey[key]);

/** Whether the URL carries an explicit Coordinate filter — the top of the tab's filter precedence. */
function hasCoordinateFilterParams(searchParams: URLSearchParams): boolean {
  return coordinateFilterParams.some((param) => searchParams.has(param));
}

/**
 * Search params for entering the Coordinate tab via a tab click, realizing the filter precedence:
 * explicitly-set shared filter params carry in (Signal keeps one provider/type context per
 * person); otherwise the remembered auto-view rides along as a `?view=` token, exactly like
 * selecting a Scheduling view; otherwise the URL is bare (the Displayed Providers lens applies).
 * Everything else (dates, the other tabs' filters, the bucket selection) is wiped — the tab never
 * inherits params it can't represent.
 */
export function getCoordinateEntrySearchParams(
  previous: URLSearchParams,
  views: InsightsSavedView[]
): URLSearchParams {
  const entryParams = new URLSearchParams();
  entryParams.set('tab', 'coordinate' satisfies HomeTab);

  for (const param of coordinateFilterParams) {
    for (const value of previous.getAll(param)) {
      entryParams.append(param, value);
    }
  }

  if (!hasCoordinateFilterParams(entryParams)) {
    const autoView = findCoordinateAutoView(views);

    if (autoView) {
      entryParams.set('view', String(autoView.id));
    }
  }

  return entryParams;
}

/**
 * For a document entry (bookmark, magic link) landing on a bare Coordinate URL: the params the
 * loader should redirect to so the remembered auto-view applies there too — same precedence as a
 * tab click, but preserving unrelated params (e.g. campaign). `null` means no redirect: the URL
 * already speaks for itself (explicit filters or a valid view token), or there is nothing
 * remembered.
 */
export function getCoordinateAutoViewRedirectParams(
  searchParams: URLSearchParams,
  views: InsightsSavedView[]
): URLSearchParams | null {
  const viewId = Number(searchParams.get('view'));
  const hasActiveView = searchParams.has('view') && views.some((view) => view.id === viewId);
  const isBareCoordinateUrl =
    searchParams.get('tab') === 'coordinate' &&
    !hasActiveView &&
    !hasCoordinateFilterParams(searchParams);

  if (!isBareCoordinateUrl) {
    return null;
  }

  const autoView = findCoordinateAutoView(views);

  if (!autoView) {
    return null;
  }

  const redirectParams = new URLSearchParams(searchParams);
  redirectParams.set('view', String(autoView.id));
  return redirectParams;
}

/**
 * Maps parsed Coordinate filter state onto the Mia feeds' filter inputs. Displayed Providers is
 * omitted so the server applies that lens; empty arrays are omitted because the wire rejects them;
 * explicit all-provider scope is preserved because it overrides that lens.
 */
interface CoordinateFeedFilterState extends Pick<
  ReturnType<typeof getParsedSearchParams>,
  'providerIds' | 'appointmentTypeIds' | 'baseAppointmentTypes'
> {
  isDisplayedProvidersScope: boolean;
}

export function toFeedFilters(parsed: CoordinateFeedFilterState): FeedFilters {
  const filters: FeedFilters = {};

  if (
    !parsed.isDisplayedProvidersScope &&
    (parsed.providerIds === allProvidersToken || parsed.providerIds.length > 0)
  ) {
    filters.providerIds = parsed.providerIds;
  }
  if (parsed.appointmentTypeIds.length > 0) filters.appointmentTypeIds = parsed.appointmentTypeIds;
  if (parsed.baseAppointmentTypes.length > 0)
    filters.baseAppointmentTypes = parsed.baseAppointmentTypes;

  return filters;
}
